import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '@/services/authApi';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { PATHS } from '@/constants/paths';
import M_LanguageSwitcher from '@/components/M_Molecules/M_LanguageSwitcher/M_LanguageSwitcher';
import A_Input from '@/components/A_Atoms/A_Input/A_Input';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';
import styles from './AuthPage.module.css';
import cn from 'classnames';
import AuthImage from '@/assets/images/A_ScreenSaver.png';

export default function ForgotPasswordPage() {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = () => {
    setCooldown(60);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || cooldown > 0) return;
    setLoading(true);
    setError(null);
    setMessage(null);
    setSuccess(false);
    try {
      const res = await authApi.passwordResetRequest(email.trim());
      setSuccess(res.success);
      setMessage(
        res.success
          ? res.data?.detail || t('auth.forgotPasswordSuccess')
          : res.message || ''
      );
      if (!res.success) setError(res.message || '');
      startCooldown();
    } catch {
      setError(t('auth.verifyEmailError'));
      startCooldown();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.action_wrapper}>
        <M_LanguageSwitcher />
        <div className={styles.sign_section}>
          <div className={styles.title_block}>
            <h1>{t('auth.forgotPasswordTitle')}</h1>
            <p className='text-p-6'>{t('auth.forgotPasswordHint')}</p>
          </div>
          <div className={styles.form_section}>
            <form onSubmit={handleSubmit} className={styles.form_inner}>
              <div className={styles.inputs_row}>
                <div className={styles.inputs_group}>
                  <A_Input
                    label={t('auth.email')}
                    type='email'
                    value={email}
                    onChange={setEmail}
                    required
                  />
                </div>
                <Link
                  to={PATHS.AUTH}
                  className={cn(styles.link_muted, 'text-p-5')}
                >
                  {t('auth.backToLogin')}
                </Link>
              </div>
              <div className={styles.button_row}>
                <A_Button
                  type='submit'
                  variant='primary'
                  size='large'
                  disabled={loading || cooldown > 0}
                >
                  {cooldown > 0
                    ? `${t('auth.submit')} (${cooldown})`
                    : t('auth.submit')}
                </A_Button>
              </div>
            </form>
            {success && message && (
              <A_Notification
                type='success'
                message={message}
                isVisible
                onClose={() => setMessage(null)}
              />
            )}
            {error && (
              <A_Notification
                type='error'
                message={error}
                isVisible
                onClose={() => setError(null)}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles.image_wrapper}>
        <div className={styles.round_decorator} />
        <img
          src={AuthImage}
          alt='Карусель с фотографиями'
          className={styles.auth_image}
        />
        <span className={cn(styles.copyright_text, 'text-p-6')}>
          2026 © Timecaps
        </span>
      </div>
    </div>
  );
}
